import React, { useId, useState } from 'react';
import Icon from '../AppIcon';

/**
 * A group of related fields.
 *
 * The setup wizard's first step asked thirteen questions in one undifferentiated
 * vertical run: what the conference is called, what kind it is, where it is,
 * when it is, what it's about, and two file uploads — all weighted identically,
 * with nothing to say where one concern ended and the next began. A form like
 * that reads as a wall, and people fill it in without ever forming a picture of
 * what they're being asked.
 *
 * Grouping is the cheapest fix: a heading, a one-line explanation of why this
 * group exists, and space around it.
 *
 * `collapsible` handles progressive disclosure. Optional groups (banner image,
 * call-for-papers document) start closed so the required path is short, and
 * open on demand. A group holding an error never stays closed — hiding the
 * reason a form won't submit is how people get stuck with no way forward.
 */
const FormSection = ({
  title,
  description,
  children,
  collapsible = false,
  defaultOpen = true,
  optional = false,
  hasError = false,
  className = '',
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  // An error inside a collapsed group must never be hidden.
  const isOpen = hasError ? true : open;

  const heading = (
    <div className="flex items-baseline gap-2 flex-wrap">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {optional && (
        <span className="text-[11px] font-medium text-muted-foreground">
          {/* Say which fields are optional rather than marking the majority
              required — the asterisks then carry no information. */}
          optional
        </span>
      )}
    </div>
  );

  return (
    <section className={`border-b border-border pb-6 last:border-0 last:pb-0 ${className}`.trim()}>
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          className="w-full flex items-start justify-between gap-3 text-start group"
        >
          <div>
            {heading}
            {description && (
              <p className="text-xs text-muted-foreground mt-0.5 max-w-prose">{description}</p>
            )}
          </div>
          <Icon
            name="ChevronDown"
            size={16}
            className={`text-muted-foreground flex-shrink-0 mt-0.5 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
      ) : (
        <div>
          {heading}
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5 max-w-prose">{description}</p>
          )}
        </div>
      )}

      {isOpen && (
        <div id={bodyId} className="mt-4 space-y-4">
          {children}
        </div>
      )}
    </section>
  );
};

export default FormSection;
